import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
    Box, Typography, CircularProgress, Alert, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Breadcrumbs, Link,
    Accordion, AccordionSummary, AccordionDetails, Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';
import { appointmentAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash'; // For accurate object comparison

// Helper to format keys like 'patientId' -> 'Patient'
const formatKey = (key) => {
    const keyMap = {
        patientId: 'Patient',
        radiologistId: 'Radiologist',
        radiologyTechnician: 'Technician',
        branch: 'Branch',
        referredBy: 'Referred By',
        scheduledAt: 'Scheduled At',
        createdAt: 'Created At',
        updatedAt: 'Updated At',
        makeHugeSale: 'Huge Sale',
        isCompleted: 'Completed',
        createdBy: 'Created By',
        salePercentage: 'Sale %',
        discountAmount: 'Discount',
        completionFee: 'Completion Fee',
    };
    // Replaces camelCase with spaces and capitalizes
    return keyMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
};

// Helper to render a single value (string, date, or populated object)
const RenderValue = ({ value }) => {
    if (value === null || typeof value === 'undefined') {
        return <Typography variant="body2" color="text.secondary">N/A</Typography>;
    }
    if (typeof value === 'object' && !Array.isArray(value) && value._id) {
        return <>{value.name || value.username || 'Object'}</>;
    }
    if (Array.isArray(value)) { // For scans array
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {value.map((item, index) => (
                    <Typography key={item._id || index} variant="body2">
                        {item.scan?.name || 'Unknown Scan'} (x{item.quantity})
                    </Typography>
                ))}
            </Box>
        );
    }
    if (['scheduledAt', 'createdAt', 'updatedAt'].some(dateKey => typeof value === 'string' && !isNaN(new Date(value).getTime()))) {
        try { return format(new Date(value), 'MMM d, yyyy, h:mm a'); } catch { /* fallback */ }
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return <>{value.toString()}</>;
};

// Component to display the "diff" view for UPDATE actions
const UpdateDiffTable = ({ before, after }) => {
    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
    const ignoredKeys = new Set(['_id', 'updatedAt', '__v', 'isActive', 'createdAt']);
    const keysToShow = [...allKeys].filter(key => !ignoredKeys.has(key));

    const changedFields = keysToShow.filter(key => !isEqual(before[key], after[key]));

    if (changedFields.length === 0) {
        return <Typography variant="body2" color="text.secondary">No changes were recorded for this update.</Typography>;
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Before</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>After</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {changedFields.map(key => (
                        <TableRow key={key}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                {formatKey(key)}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                                <RenderValue value={before[key]} />
                            </TableCell>
                            <TableCell>
                                <RenderValue value={after[key]} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// Component to display the full state for CREATE actions
const CreationChangesTable = ({ data }) => {
    const ignoredKeys = new Set(['_id', 'isActive']);
    const keysToShow = Object.keys(data).filter(key => !ignoredKeys.has(key));

    return (
        <TableContainer>
            <Table size="small">
                <TableBody>
                    {keysToShow.map(key => (
                        <TableRow key={key}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '30%' }}>
                                {formatKey(key)}
                            </TableCell>
                            <TableCell><RenderValue value={data[key]} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// Main component that decides which details view to show
const ChangesDetail = ({ action, changes }) => {
    if (action === 'CREATE') {
        return <CreationChangesTable data={changes} />;
    }
    if (action === 'UPDATE' && changes.before && changes.after) {
        return <UpdateDiffTable before={changes.before} after={changes.after} />;
    }
    return <Typography variant="body2">No changes to display for this action type.</Typography>;
};

// --- MAIN APPOINTMENT HISTORY COMPONENT ---
const AppointmentHistory = () => {
    const { id } = useParams();
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(''); // FIX: Added error state

    // FIX: Added full implementation for fetching data
    const fetchHistory = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await appointmentAPI.getAppointmentHistory(id);
            setHistory(response.data.data);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to fetch appointment history';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return (
        <Paper sx={{ p: 3, m: 2, overflowX: 'auto' }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/appointments">
                    Appointments
                </Link>
                <Typography color="text.primary">History ({id})</Typography>
            </Breadcrumbs>

            <Typography variant="h4" gutterBottom>Appointment Change History</Typography>

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

            {!loading && !error && (
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.length > 0 ? (
                                history.map((entry) => (
                                    <TableRow key={entry._id} hover>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {format(new Date(entry.createdAt), 'PPpp')}
                                        </TableCell>
                                        <TableCell>{entry.user?.name || entry.user?.username || 'N/A'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={entry.action}
                                                size="small"
                                                color={entry.action === 'CREATE' ? 'success' : 'info'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Accordion elevation={1}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Typography variant="body2">View Changes</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 1 }}>
                                                    <ChangesDetail action={entry.action} changes={entry.changes} />
                                                </AccordionDetails>
                                            </Accordion>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No history found for this appointment.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default AppointmentHistory;